import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography, Button, Container } from "@mui/material";
import InstagramIcon from '@mui/icons-material/Instagram';
import Hamburger from "./Hamburger";

const Header = () => {
  const location = useLocation();
  const [menuState, setMenuState] = useState({
    clicked: false,
    menuName: "",
  });
  const [disabled, setDisabled] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuState({
      clicked: false,
      menuName: "Menu",
    });
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenu = () => {
    disableMenu();
    setMenuState((prevState) => ({
      clicked: !prevState.clicked,
      menuName: prevState.clicked ? "Menu" : "Close",
    }));
  };

  const disableMenu = () => {
    setDisabled(true);
    setTimeout(() => {
      setDisabled(false);
    }, 800);
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          background: scrolled
            ? 'rgba(10, 10, 10, 0.98)'
            : 'transparent',
          color: "#fff",
          position: 'fixed',
          width: '100%',
          top: 0,
          zIndex: 1000,
          height: { xs: '50px', md: '55px' },
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          backdropFilter: scrolled ? 'blur(30px)' : 'blur(10px)',
          borderBottom: scrolled ? '1px solid rgba(255, 77, 77, 0.1)' : '1px solid transparent',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: scrolled ? '80%' : '0%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255, 77, 77, 0.5), rgba(249, 203, 40, 0.5), transparent)',
            transition: 'width 0.6s ease',
          }
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pt: { xs: 0.5, md: 0.5 },
              pb: { xs: 0.5, md: 0.5 },
              height: { xs: '45px', md: '50px' },
            }}
          >
            {/* Logo with Icon */}
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  position: 'relative',
                }}
              >

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.3rem', md: '1.6rem' },
                      letterSpacing: '0.5px',
                      background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1,
                      mb: 0.1,
                    }}
                  >
                    KJ GROUP
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      color: 'rgba(255,255,255,0.5)',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    Asian Cuisine
                  </Typography>
                </Box>
              </Box>
            </Link>

            {/* Right Side - Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {/* Instagram */}
              <Button
                component="a"
                href="https://www.instagram.com/kjgroup.uk"
                target="_blank"
                sx={{
                  minWidth: 'auto',
                  width: { xs: '36px', md: '40px' },
                  height: { xs: '36px', md: '40px' },
                  padding: 0,
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #833AB4, #E1306C)',
                    borderColor: 'transparent',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 24px rgba(225, 48, 108, 0.3)',
                  }
                }}
              >
                <InstagramIcon sx={{ fontSize: { xs: '18px', md: '20px' } }} />
              </Button>

              {/* Menu Toggle */}
              <Button
                onClick={handleMenu}
                disabled={disabled}
                sx={{
                  minWidth: { xs: '90px', md: '110px' },
                  height: { xs: '40px', md: '44px' },
                  borderRadius: '8px',
                  background: menuState.clicked
                    ? 'linear-gradient(135deg, #FF4D4D, #F9CB28)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: menuState.clicked ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  fontWeight: 700,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    background: menuState.clicked
                      ? 'linear-gradient(135deg, #F9CB28, #FF4D4D)'
                      : 'rgba(255, 255, 255, 0.08)',
                    transform: 'translateY(-3px)',
                    boxShadow: menuState.clicked
                      ? '0 8px 24px rgba(255, 77, 77, 0.4)'
                      : '0 8px 24px rgba(0, 0, 0, 0.3)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: menuState.clicked ? '0' : '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transition: 'left 0.5s ease',
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      width: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        width: menuState.clicked ? '10px' : '10px',
                        height: '1.5px',
                        background: '#fff',
                        borderRadius: '2px',
                        transition: 'all 0.3s ease',
                        transform: menuState.clicked ? 'rotate(45deg) translateY(3.5px)' : 'none',
                      }}
                    />
                    <Box
                      sx={{
                        width: menuState.clicked ? '0px' : '7px',
                        height: '1.5px',
                        background: '#fff',
                        borderRadius: '2px',
                        transition: 'all 0.3s ease',
                        opacity: menuState.clicked ? 0 : 1,
                      }}
                    />
                    <Box
                      sx={{
                        width: menuState.clicked ? '10px' : '10px',
                        height: '1.5px',
                        background: '#fff',
                        borderRadius: '2px',
                        transition: 'all 0.3s ease',
                        transform: menuState.clicked ? 'rotate(-45deg) translateY(-3.5px)' : 'none',
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.6rem', md: '0.65rem' },
                      fontWeight: 700,
                      letterSpacing: '1px',
                    }}
                  >
                    {menuState.clicked ? 'CLOSE' : 'MENU'}
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Hamburger state={menuState} handleMenu={handleMenu} />
    </>
  );
};

export default Header;